import { Badge, Eyebrow, MediumText, StrongText, Subheading, SurfaceCard, SurfacePanel, SurfaceRow } from '../../../components/ui'
import { FITNESS_SUMMARY, RECENT_WORKOUTS, WEEKLY_ACTIVITY } from '../constants'
import { uiClasses } from '../../shared/uiClasses'
import { GradientSphereMetricCard, sphereColorAt } from '../components/GradientSphereCard'

const TEXT_SUBTLE = uiClasses.textSubtle
const TEXT_FAINT = uiClasses.textFaint

const MAX_STEPS = Math.max(...WEEKLY_ACTIVITY.map((day) => day.steps))

const SUMMARY_STATS = [
  { label: 'Weekly Active Minutes', value: FITNESS_SUMMARY.weeklyActiveMinutes, unit: 'min' },
  { label: 'Zone 2 Minutes', value: FITNESS_SUMMARY.weeklyZone2Minutes, unit: 'min' },
  { label: 'Avg Daily Steps', value: FITNESS_SUMMARY.avgDailySteps.toLocaleString(), unit: '' },
  { label: 'VO2 Max', value: FITNESS_SUMMARY.vo2Max, unit: 'mL/kg/min' },
  { label: 'Resting HR', value: FITNESS_SUMMARY.restingHR, unit: 'bpm' },
  { label: 'Fitness Age', value: FITNESS_SUMMARY.fitnessAge, unit: 'yrs' },
]

export default function FitnessTab() {
  const totalWorkoutMinutes = RECENT_WORKOUTS.reduce(
    (sum, workout) => sum + Number.parseInt(workout.duration, 10),
    0
  )
  const totalWorkoutCalories = RECENT_WORKOUTS.reduce((sum, workout) => sum + workout.calories, 0)
  const peakStepDay = WEEKLY_ACTIVITY.reduce((peak, day) =>
    day.steps > peak.steps ? day : peak
  )

  return (
    <div className="space-y-6">
      <SurfaceCard as="article" className="fitness-overview-shell">
        <div className="fitness-overview-layout">
          <div>
            <div className="flex flex-wrap items-center gap-2">
              <Subheading variant="dashboard">Fitness command center</Subheading>
              <Badge variant="zinc">
                {RECENT_WORKOUTS.length} recent workouts
              </Badge>
            </div>
            <div className="mt-4 flex flex-wrap items-center gap-2">
              <Badge variant="zinc">
                {totalWorkoutMinutes} active min logged
              </Badge>
              <Badge variant="zinc">
                {totalWorkoutCalories.toLocaleString()} kcal burned
              </Badge>
            </div>
          </div>

          <div className="fitness-summary-grid">
            {SUMMARY_STATS.map((stat, index) => (
              <GradientSphereMetricCard
                key={stat.label}
                label={stat.label}
                sphereColor={sphereColorAt(index)}
                value={
                  <>
                    {stat.value}
                    {stat.unit && <span className="gradient-sphere-metric-card__unit">{stat.unit}</span>}
                  </>
                }
              />
            ))}
          </div>
        </div>
      </SurfaceCard>

      <SurfaceCard as="article">
        <div className="flex flex-wrap items-center justify-between gap-3">
          <Subheading variant="dashboard">Weekly activity</Subheading>
          <Badge variant="zinc">Peak day: {peakStepDay.day}</Badge>
        </div>

        <div className="fitness-activity-layout mt-5">
          <div className="flex h-56 items-end gap-3">
            {WEEKLY_ACTIVITY.map((day) => {
              const height = Math.max(12, (day.steps / MAX_STEPS) * 100)
              return (
                <div key={day.day} className="flex flex-1 flex-col items-center gap-2">
                  <div
                    className="w-full rounded-t-xl bg-linear-to-t from-accent to-accent-soft"
                    style={{ height: `${height}%` }}
                  />
                  <MediumText as="span" size="xs" tone="secondary">{day.day}</MediumText>
                  <span className={`text-[11px] ${TEXT_FAINT}`}>{day.activeMin} min</span>
                </div>
              )
            })}
          </div>

          <aside className="fitness-activity-side space-y-3">
            <SurfacePanel>
              <Eyebrow variant="mini" tone="subtle">Peak Steps</Eyebrow>
              <StrongText className="mt-2" size="base">{peakStepDay.steps.toLocaleString()}</StrongText>
              <p className={`mt-1 text-sm/6 ${TEXT_SUBTLE}`}>{peakStepDay.day} was your strongest step day this week.</p>
            </SurfacePanel>

            <SurfacePanel>
              <Eyebrow variant="mini" tone="subtle">Zone 2 Focus</Eyebrow>
              <StrongText className="mt-2" size="base">{FITNESS_SUMMARY.weeklyZone2Minutes} minutes</StrongText>
              <p className={`mt-1 text-sm/6 ${TEXT_SUBTLE}`}>Steady aerobic base work this week.</p>
            </SurfacePanel>
          </aside>
        </div>
      </SurfaceCard>

      <SurfaceCard as="article">
        <div className="flex flex-wrap items-center justify-between gap-3">
          <Subheading variant="dashboard">Workout stream</Subheading>
          <Badge variant="zinc">
            Avg {Math.round(totalWorkoutMinutes / RECENT_WORKOUTS.length)} min / session
          </Badge>
        </div>

        <div className="fitness-workout-stream mt-4">
          {RECENT_WORKOUTS.map((workout) => (
            <SurfaceRow key={workout.id}>
              <div className="flex items-start justify-between gap-4">
                <div className="flex-1 space-y-1">
                  <div className="flex items-center gap-2">
                    <Badge variant="zinc">{workout.type}</Badge>
                    <StrongText as="span">{workout.title}</StrongText>
                  </div>
                  <div className={`flex flex-wrap gap-x-4 gap-y-1 text-xs/5 ${TEXT_SUBTLE}`}>
                    <span>{workout.duration}</span>
                    <span>{workout.calories} cal</span>
                    <span>Avg HR {workout.avgHR} bpm</span>
                    {workout.distance && <span>{workout.distance}</span>}
                  </div>
                </div>
                <span className={`shrink-0 text-xs/5 ${TEXT_FAINT}`}>{workout.date}</span>
              </div>
            </SurfaceRow>
          ))}
        </div>
      </SurfaceCard>
    </div>
  )
}
